import { DependencyList, useMemo } from "react";

import { debounce, memoize } from "lodash-es";
import type { DebouncedFunc } from "lodash-es";

import { ParametersExceptFirst } from "../ts";

import useDynamicCallback from "./useDynamicCallback";

export interface MemoizeDebouncedFunction<F extends (...args: Parameters<F>) => ReturnType<F>> {
  (...args: Parameters<F>): void;
  flush: (...args: Parameters<F>) => void;
}

/** Credits: https://github.com/lodash/lodash/issues/2403#issuecomment-1158234729
 *
 * Usage example:
 *
 * `const debouncedFunction = memoizeDebounce(
 *   (firstName: string, lastName:string, message: string) => {
 *     console.log(`${message}`);
 *   },
 *   1000,
 *   {}, // Debounce options here
 *   (firstName, lastName) => (firstName + "-" + lastName)
 * );`
 *
 * debouncedFunction("dave", "smith","Hey Jim")
 * debouncedFunction("tom", "smith","Hey Tom")
 * debouncedFunction("dave", "smith","Hey Dave")
 *
 * // Console output:
 * // "Hey Tom"
 * // "Hey Dave"
 *
 * // "Hey Jim won't show as it was debounced as it had the same "firstName" and "lastName" as the 3rd call.
 */
export function memoizeDebounce<F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  resolver: (...args: Parameters<F>) => unknown,
  debounceOptions: ParametersExceptFirst<typeof debounce>
): MemoizeDebouncedFunction<F> {
  const debounceMemo = memoize<(...args: Parameters<F>) => DebouncedFunc<F>>(
    () => debounce(func, ...debounceOptions),
    resolver
  );

  function wrappedFunction(...args: Parameters<F>): ReturnType<F> | undefined {
    return debounceMemo(...args)(...args);
  }

  wrappedFunction.flush = (...args: Parameters<F>): void => {
    debounceMemo(...args).flush();
  };

  return wrappedFunction as unknown as MemoizeDebouncedFunction<F>;
}

const useMemoizedDebouncedCallback = <F extends (...args: Parameters<F>) => ReturnType<F>>(
  func: F,
  debounceOptions: ParametersExceptFirst<typeof debounce>,
  resolver: (...args: Parameters<F>) => unknown,
  deps?: DependencyList
) => {
  const internalCallback = useDynamicCallback(func); // this allows using debounced callback without nested useCallback

  return useMemo(
    () => memoizeDebounce(internalCallback, resolver, debounceOptions),
    [...debounceOptions, ...(deps ?? [])] // eslint-disable-line
  );
};

export default useMemoizedDebouncedCallback;
